/**
 * Recent Pet Finder Table Component
 * - Displays recent lost/found pet reports on dashboard
 * - Shows pet information, location, and posting details
 * - Links to detailed pet finder views
 */

import React from "react";
import { formatDateTime } from "../../details/Shared/helpers";

const Badge = ({ status }) => {
  const ok = status === "Active";
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"
      }`}
    >
      {status}
    </span>
  );
};

const PetCard = ({ pet, location, contact, reward, status }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 last:mb-0">
    <div className="flex justify-between items-start mb-2">
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{pet}</h4>
        <p className="text-xs text-gray-500 truncate">{location}</p>
      </div>
      <Badge status={status} />
    </div>
    <div className="flex justify-between items-center text-xs text-gray-600">
      <span>Contact: {contact}</span>
      <span className="font-medium text-green-600">{reward}</span>
    </div>
  </div>
);
export default function PetFinderTable({ petFinderData }) {
  const rows = petFinderData && petFinderData.length > 0
    ? petFinderData.map(item => ({
        parent: item.pet_name ? `${item.breed} - ${item.pet_name}` : 'N/A',
        location: item.location || 'N/A',
        contact: item.pet_parent || 'N/A', // API doesn't provide contact info
        reward: item.reward ? `₹${item.reward}` : 'N/A',
        status: item.status || 'N/A',
        submitted: formatDateTime(item.submitted)
      }))
    : [];

  if (!petFinderData || petFinderData.length === 0) {
    return (
      <div className="overflow-hidden border-gray-300 border p-3 sm:p-4 py-4 sm:py-5 rounded-lg">
        <div className="mb-4">
          <div className="text-base sm:text-lg font-semibold text-gray-900">Pet Finder</div>
          <div className="mt-1 text-xs sm:text-sm text-gray-500">
            Manage lost & found pet listings
          </div>
        </div>
        <div className="text-center py-8 text-gray-500">
          No pet finder data available
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden border-gray-300 border p-3 sm:p-4 py-4 sm:py-5 rounded-lg">
      <div className="mb-4">
        <div className="text-base sm:text-lg font-semibold text-gray-900">Pet Finder</div>
        <div className="mt-1 text-xs sm:text-sm text-gray-500">
          Manage lost & found pet listings
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-1">
        {rows.map((r, i) => (
          <PetCard
            key={i}
            pet={r.parent}
            location={r.location}
            contact={r.contact}
            reward={r.reward}
            status={r.status}
          />
        ))}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500">
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Pet</th>
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Location</th>
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Contact</th>
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Reward</th>
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Posted On</th>
              <th className="py-3 pr-2 sm:pr-4 text-xs sm:text-sm">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <tr key={i} className="text-xs sm:text-sm text-gray-700">
                <td className="py-3 pr-2 sm:pr-4">{r.parent}</td>
                <td className="py-3 pr-2 sm:pr-4">{r.location}</td>
                <td className="py-3 pr-2 sm:pr-4">{r.contact}</td>
                <td className="py-3 pr-2 sm:pr-4">{r.reward}</td>
                <td className="py-3 pr-2 sm:pr-4">{r.submitted}</td>
                <td className="py-3 pr-2 sm:pr-4">
                  <Badge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
